import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Autocomplete,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Switch,
  FormControlLabel,
  Paper,
  alpha,
  IconButton,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Group as GroupIcon,
  Repeat as RepeatIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { format, addMinutes, addDays, addWeeks, startOfWeek, isBefore } from 'date-fns';
import { es } from 'date-fns/locale';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import citaService from '../../services/citaService';
import pacienteService from '../../services/pacienteService';
import etapaTratamientoService from '../../services/etapaTratamientoService';
import { esFeriadoPeruano, obtenerNombreFeriado } from '../../utils/feriadosPeruanos';

const DIAS_SEMANA = [
  { valor: 1, nombre: 'Lunes', abrev: 'L' },
  { valor: 2, nombre: 'Martes', abrev: 'M' },
  { valor: 3, nombre: 'Miércoles', abrev: 'X' },
  { valor: 4, nombre: 'Jueves', abrev: 'J' },
  { valor: 5, nombre: 'Viernes', abrev: 'V' },
  { valor: 6, nombre: 'Sábado', abrev: 'S' },
  { valor: 0, nombre: 'Domingo', abrev: 'D' }
];

const QuickCreateModal = ({
  open,
  onClose,
  onSuccess,
  selectedEspecialista,
  selectedEspecialidad,
  preselectedDate,
  preselectedHour,
  showNotification
}) => {
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [loadingPacientes, setLoadingPacientes] = useState(false);
  const [loadingEtapas, setLoadingEtapas] = useState(false);
  
  // Datos
  const [pacientes, setPacientes] = useState([]);
  const [especialidadesFiltradas, setEspecialidadesFiltradas] = useState([]);
  const [etapas, setEtapas] = useState([]);
  
  // Selecciones
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [selectedEspecialidadLocal, setSelectedEspecialidadLocal] = useState(null);
  const [pacientesSeleccionados, setPacientesSeleccionados] = useState([]);
  
  // Tipos de cita
  const [tipoCita, setTipoCita] = useState('individual');
  
  // Sistema de patrones recurrentes
  const [patronesRecurrentes, setPatronesRecurrentes] = useState([]);
  const [fechaInicioRecurrencia, setFechaInicioRecurrencia] = useState(null);
  const [nuevoPatron, setNuevoPatron] = useState({
    diaSemana: '',
    horaInicio: null,
    horaFin: null,
    cantidadSesiones: 8
  });

  const isInitialized = useRef(false);

  // ===== FUNCIONES DE VALIDACIÓN =====

  // ✅ NUEVA: Validar que una fecha no sea feriado
  const validarFechaNoFeriado = (fecha) => {
    if (!fecha) return { valida: true };
    
    const fechaStr = format(fecha, 'yyyy-MM-dd');
    const esFeriado = esFeriadoPeruano(fechaStr);
    
    if (esFeriado) {
      const nombreFeriado = obtenerNombreFeriado(fechaStr);
      return {
        valida: false,
        mensaje: `${format(fecha, 'dd/MM/yyyy')} es feriado: ${nombreFeriado}`
      };
    }
    
    return { valida: true };
  };

  // ✅ NUEVA: Validar todos los patrones recurrentes
  const validarPatronesNoTienenFeriados = () => {
    if (patronesRecurrentes.length === 0 || !fechaInicioRecurrencia) {
      return { validos: true };
    }

    const todasLasFechas = generarCitasDesdePatrones();
    const feriadosEncontrados = [];

    todasLasFechas.forEach(citaInfo => {
      const fechaStr = format(citaInfo.fecha, 'yyyy-MM-dd');
      if (esFeriadoPeruano(fechaStr)) {
        feriadosEncontrados.push({
          fecha: format(citaInfo.fecha, 'dd/MM/yyyy'),
          patron: citaInfo.patron.diaNombre,
          sesion: citaInfo.numeroSesion,
          nombreFeriado: obtenerNombreFeriado(fechaStr)
        });
      }
    });

    if (feriadosEncontrados.length > 0) {
      return {
        validos: false,
        feriados: feriadosEncontrados
      };
    }

    return { validos: true };
  };

  // ===== FUNCIONES DE CARGA DE DATOS =====

  const cargarEtapasDeEspecialidad = useCallback(async (especialidadId, pacienteId = null) => {
    try {
      setLoadingEtapas(true);
      let response;
      
      if (pacienteId) {
        response = await etapaTratamientoService.getEtapasDisponiblesPaciente(pacienteId, especialidadId);
      } else {
        response = await etapaTratamientoService.getByEspecialidad(especialidadId);
      }
      
      const etapasData = Array.isArray(response.data) ? response.data : [];
      setEtapas(etapasData);
    } catch (error) {
      console.error('Error cargando etapas:', error);
      setEtapas([]);
      showNotification?.('Error al cargar etapas de tratamiento', 'error');
    } finally {
      setLoadingEtapas(false);
    }
  }, [showNotification]);

  const cargarPacientesDelEspecialista = useCallback(async (especialistaId) => {
    try {
      setLoadingPacientes(true);
      const response = await pacienteService.getPacientesByEspecialista(parseInt(especialistaId, 10));
      const pacientesData = Array.isArray(response.data) ? response.data : [];
      setPacientes(pacientesData);
    } catch (error) {
      console.error('Error cargando pacientes:', error);
      setPacientes([]);
      showNotification?.('Error al cargar pacientes del especialista', 'error');
    } finally {
      setLoadingPacientes(false);
    }
  }, [showNotification]);

  // Generar fechas desde patrones
  const generarCitasDesdePatrones = useCallback(() => {
  if (!fechaInicioRecurrencia || patronesRecurrentes.length === 0) return [];

  const todasLasCitas = [];

  patronesRecurrentes.forEach((patron) => {
    // ✅ CORREGIDO: Empezar desde fechaInicioRecurrencia
    let fechaActual = new Date(fechaInicioRecurrencia);
    
    // ✅ Buscar el próximo día que coincida con el patrón
    // (puede ser el mismo día si coincide)
    while (fechaActual.getDay() !== patron.diaSemana) {
      fechaActual = addDays(fechaActual, 1);
    }
    
    // 🔍 DEBUG (TEMPORAL - puedes borrar después)
    console.log('🔍 Patrón:', patron.diaNombre, 
                '| Fecha inicio:', format(fechaInicioRecurrencia, 'dd/MM/yyyy'),
                '| Primera sesión:', format(fechaActual, 'dd/MM/yyyy'));

    for (let i = 0; i < patron.cantidadSesiones; i++) {
      const fechaCita = new Date(fechaActual);
      fechaCita.setHours(patron.horaInicio.getHours(), patron.horaInicio.getMinutes(), 0, 0);

      const fechaFin = new Date(fechaCita);
      fechaFin.setHours(patron.horaFin.getHours(), patron.horaFin.getMinutes(), 0, 0);

      todasLasCitas.push({
        fecha: fechaCita,
        horaInicio: fechaCita,
        horaFin: fechaFin,
        patron: patron,
        numeroSesion: i + 1
      });

      fechaActual = addWeeks(fechaActual, 1);
    }
  });

  return todasLasCitas.sort((a, b) => a.fecha - b.fecha);
}, [fechaInicioRecurrencia, patronesRecurrentes]);

  // Agregar patrón
  const handleAgregarPatron = () => {
    if (!nuevoPatron.diaSemana || !nuevoPatron.horaInicio || !nuevoPatron.horaFin || !nuevoPatron.cantidadSesiones) {
      showNotification?.('Complete todos los campos del patrón', 'warning');
      return;
    }

    const existeElDia = patronesRecurrentes.some(p => p.diaSemana === nuevoPatron.diaSemana);
    if (existeElDia) {
      showNotification?.('Ya existe un patrón para ese día de la semana', 'warning');
      return;
    }

    const patronFormateado = {
      id: Date.now(),
      diaSemana: nuevoPatron.diaSemana,
      diaNombre: DIAS_SEMANA.find(d => d.valor === nuevoPatron.diaSemana)?.nombre,
      horaInicio: nuevoPatron.horaInicio,
      horaFin: nuevoPatron.horaFin,
      cantidadSesiones: nuevoPatron.cantidadSesiones
    };

    setPatronesRecurrentes([...patronesRecurrentes, patronFormateado]);

    setNuevoPatron({
      diaSemana: '',
      horaInicio: null,
      horaFin: null,
      cantidadSesiones: 8
    });
  };

  const handleEliminarPatron = (patronId) => {
    setPatronesRecurrentes(patronesRecurrentes.filter(p => p.id !== patronId));
  };

  // ===== VALIDACIÓN Y FORMIK =====

  const validationSchema = Yup.object({
    fecha: Yup.date().nullable(),
    hora_inicio: Yup.date().nullable(),
    hora_fin: Yup.date().nullable(),
    motivo: Yup.string(),
    especialidad_id: Yup.string().required('Especialidad es requerida'),
    etapa_id: Yup.string().required('Etapa es requerida'),
    paciente_id: tipoCita.includes('individual') 
      ? Yup.string().required('Paciente es requerido')
      : Yup.string().nullable()
  });

  const formik = useFormik({
    initialValues: {
      paciente_id: '',
      especialista_id: typeof selectedEspecialista === 'object' ? selectedEspecialista?.id : selectedEspecialista,
      especialidad_id: selectedEspecialidad?.id || '',
      etapa_id: '',
      fecha: preselectedDate || new Date(),
      hora_inicio: preselectedHour !== null ? 
        new Date(new Date().setHours(preselectedHour, 0, 0, 0)) : 
        new Date(new Date().setHours(9, 0, 0, 0)),
      hora_fin: preselectedHour !== null ? 
        new Date(new Date().setHours(preselectedHour + 1, 0, 0, 0)) : 
        new Date(new Date().setHours(10, 0, 0, 0)),
      tipo: 'Seguimiento',
      estado: 'Programada',
      motivo: '',
      notas: ''
    },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: false
  });

  // ===== EFECTOS =====

  useEffect(() => {
    if (open && selectedEspecialista && !isInitialized.current) {
      isInitialized.current = true;
      
      const especialistaObj = typeof selectedEspecialista === 'object' ? selectedEspecialista : null;
      const especialistaId = especialistaObj?.id || selectedEspecialista;
      
      formik.setFieldValue('especialista_id', especialistaId);
      
      // ✅ CORREGIDO: Preselección de fecha y hora
      if (preselectedDate) {
        formik.setFieldValue('fecha', preselectedDate);
      }
      
      if (preselectedHour !== null && preselectedHour !== undefined) {
        const horaInicio = new Date();
        horaInicio.setHours(preselectedHour, 0, 0, 0);
        const horaFin = new Date();
        horaFin.setHours(preselectedHour + 1, 0, 0, 0);
        
        formik.setFieldValue('hora_inicio', horaInicio);
        formik.setFieldValue('hora_fin', horaFin);
      }
      
      if (especialistaObj?.especialidades) {
        setEspecialidadesFiltradas(especialistaObj.especialidades);
        
        if (selectedEspecialidad) {
          setSelectedEspecialidadLocal(selectedEspecialidad);
          formik.setFieldValue('especialidad_id', selectedEspecialidad.id);
        }
      }
      
      cargarPacientesDelEspecialista(especialistaId);
    }
    
    if (!open) {
      isInitialized.current = false;
    }
  }, [open, selectedEspecialista, selectedEspecialidad, preselectedDate, preselectedHour, cargarPacientesDelEspecialista]);

  useEffect(() => {
    if (selectedEspecialidadLocal?.id) {
      const pacienteId = selectedPaciente?.id || null;
      cargarEtapasDeEspecialidad(selectedEspecialidadLocal.id, pacienteId);
    }
  }, [selectedEspecialidadLocal, selectedPaciente, cargarEtapasDeEspecialidad]);

  // ===== HANDLERS =====

  const handlePacienteChange = (event, newValue) => {
    setSelectedPaciente(newValue);
    formik.setFieldValue('paciente_id', newValue?.id || '');
  };

  const handleEspecialidadChange = (event) => {
    const especialidadId = event.target.value;
    const especialidad = especialidadesFiltradas.find(e => e.id === Number(especialidadId));
    
    setSelectedEspecialidadLocal(especialidad);
    formik.setFieldValue('especialidad_id', especialidadId);
    formik.setFieldValue('etapa_id', '');
    setEtapas([]);
    
    if (especialidad?.duracion_cita_defecto && formik.values.hora_inicio) {
      const horaFin = addMinutes(formik.values.hora_inicio, especialidad.duracion_cita_defecto);
      formik.setFieldValue('hora_fin', horaFin);
    }
  };

  const handleHoraInicioChange = (hora) => {
    formik.setFieldValue('hora_inicio', hora);
    if (hora) {
      const horaFin = addMinutes(hora, 60);
      formik.setFieldValue('hora_fin', horaFin);
    }
  };

  // ===== FUNCIONES DE CREACIÓN =====

  async function handleSubmit(values) {
    // ✅ VALIDAR FERIADOS antes de enviar
    if (!tipoCita.includes('recurrente')) {
      const validacion = validarFechaNoFeriado(values.fecha);
      if (!validacion.valida) {
        showNotification?.(validacion.mensaje, 'error');
        return;
      }
    } else {
      const validacion = validarPatronesNoTienenFeriados();
      if (!validacion.validos) {
        const mensajeError = `Se detectaron ${validacion.feriados.length} feriados en las fechas:\n` +
          validacion.feriados.slice(0, 3).map(f => `• ${f.fecha} (${f.nombreFeriado})`).join('\n') +
          (validacion.feriados.length > 3 ? `\n... y ${validacion.feriados.length - 3} más` : '');
        
        showNotification?.(mensajeError, 'error');
        return;
      }
    }

    setLoading(true);
    try {
      if (tipoCita === 'individual') {
        await crearCitaIndividual(values);
      } else if (tipoCita === 'grupal') {
        await crearCitaGrupal(values);
      } else if (tipoCita === 'recurrente-individual') {
        await crearCitasRecurrentesIndividual(values);
      } else if (tipoCita === 'recurrente-grupal') {
        await crearCitasRecurrentesGrupal(values);
      }
    } catch (error) {
      console.error('Error creando citas:', error);
      showNotification?.(
        error.response?.data?.message || 'Error al crear las citas', 
        'error'
      );
    } finally {
      setLoading(false);
    }
  }

  const crearCitaIndividual = async (values) => {
    const citaData = {
      ...values,
      fecha: format(values.fecha, 'yyyy-MM-dd'),
      hora_inicio: format(values.hora_inicio, 'HH:mm:ss'),
      hora_fin: format(values.hora_fin, 'HH:mm:ss'),
    };

    const response = await citaService.createCita(citaData);
    showNotification?.('Cita creada exitosamente', 'success');
    onSuccess?.(response.data);
    handleClose();
  };

  const crearCitaGrupal = async (values) => {
    if (pacientesSeleccionados.length === 0) {
      showNotification?.('Debe seleccionar al menos un paciente', 'error');
      return;
    }

    const citaGrupalData = {
      pacientes_ids: pacientesSeleccionados.map(p => p.id),
      especialista_id: values.especialista_id,
      especialidad_id: values.especialidad_id,
      etapa_id: values.etapa_id,
      fecha: format(values.fecha, 'yyyy-MM-dd'),
      hora_inicio: format(values.hora_inicio, 'HH:mm:ss'),
      hora_fin: format(values.hora_fin, 'HH:mm:ss'),
      tipo: values.tipo,
      estado: values.estado,
      motivo: values.motivo,
      notas: values.notas
    };

    const response = await citaService.createCitaGrupal(citaGrupalData);
    showNotification?.(`Cita grupal creada para ${pacientesSeleccionados.length} pacientes`, 'success');
    onSuccess?.(response.data);
    handleClose();
  };

  const crearCitasRecurrentesIndividual = async (values) => {
    const todasLasCitas = generarCitasDesdePatrones();
    
    if (todasLasCitas.length === 0) {
      showNotification?.('Configure al menos un patrón de recurrencia', 'error');
      return;
    }

    let citasCreadas = 0;
    
    for (const citaInfo of todasLasCitas) {
      try {
        const citaData = {
          ...values,
          fecha: format(new Date(citaInfo.fecha.getFullYear(), citaInfo.fecha.getMonth(), citaInfo.fecha.getDate()), 'yyyy-MM-dd'),
          hora_inicio: format(citaInfo.horaInicio, 'HH:mm:ss'),
          hora_fin: format(citaInfo.horaFin, 'HH:mm:ss'),
          motivo: `${values.motivo} - ${citaInfo.patron.diaNombre} Sesión ${citaInfo.numeroSesion}`,
          notas: `${values.notas} | Patrón recurrente: ${citaInfo.patron.diaNombre} sesión ${citaInfo.numeroSesion}`
        };

        await citaService.createCita(citaData);
        citasCreadas++;
      } catch (error) {
        console.error(`Error en fecha ${format(citaInfo.fecha, 'dd/MM/yyyy')}:`, error);
      }
    }

    showNotification?.(`${citasCreadas} citas recurrentes creadas`, 'success');
    onSuccess?.({ refresh: true });
    handleClose();
  };

  const crearCitasRecurrentesGrupal = async (values) => {
    if (pacientesSeleccionados.length === 0) {
      showNotification?.('Debe seleccionar al menos un paciente', 'error');
      return;
    }

    const todasLasCitas = generarCitasDesdePatrones();
    
    if (todasLasCitas.length === 0) {
      showNotification?.('Configure al menos un patrón de recurrencia', 'error');
      return;
    }

    const pacientesIds = pacientesSeleccionados.map(p => p.id);
    let sesionesCreadas = 0;
    
    for (const citaInfo of todasLasCitas) {
      try {
        const citaGrupalData = {
          pacientes_ids: pacientesIds,
          especialista_id: values.especialista_id,
          especialidad_id: values.especialidad_id,
          etapa_id: values.etapa_id,
          fecha: format(new Date(citaInfo.fecha.getFullYear(), citaInfo.fecha.getMonth(), citaInfo.fecha.getDate()), 'yyyy-MM-dd'),
          hora_inicio: format(citaInfo.horaInicio, 'HH:mm:ss'),
          hora_fin: format(citaInfo.horaFin, 'HH:mm:ss'),
          tipo: values.tipo,
          estado: values.estado,
          motivo: `${values.motivo} - ${citaInfo.patron.diaNombre} Sesión ${citaInfo.numeroSesion}`,
          notas: `${values.notas} | Patrón recurrente grupal: ${citaInfo.patron.diaNombre} sesión ${citaInfo.numeroSesion}`
        };

        await citaService.createCitaGrupal(citaGrupalData);
        sesionesCreadas++;
      } catch (error) {
        console.error(`Error en fecha grupal ${format(citaInfo.fecha, 'dd/MM/yyyy HH:mm')}:`, error);
      }
    }

    showNotification?.(`${sesionesCreadas} sesiones grupales recurrentes creadas para ${pacientesIds.length} pacientes`, 'success');
    onSuccess?.({ refresh: true });
    handleClose();
  };

  const handleClose = () => {
    formik.resetForm();
    setSelectedPaciente(null);
    setSelectedEspecialidadLocal(null);
    setPacientes([]);
    setEtapas([]);
    setPacientesSeleccionados([]);
    setTipoCita('individual');
    setPatronesRecurrentes([]);
    setFechaInicioRecurrencia(null);
    setNuevoPatron({
      diaSemana: '',
      horaInicio: null,
      horaFin: null,
      cantidadSesiones: 8
    });
    onClose();
  };

  // Calcular totales para preview
  const totalSesiones = patronesRecurrentes.reduce((total, patron) => total + patron.cantidadSesiones, 0);
  const previewFechas = generarCitasDesdePatrones();
  const validacionPatrones = validarPatronesNoTienenFeriados();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">
              Crear Cita Rápida
            </Typography>
          </Stack>
          
          <Box sx={{ mt: 1 }}>
            <Chip
              icon={<PersonIcon />}
              label={typeof selectedEspecialista === 'object' ? 
                `${selectedEspecialista?.usuario?.nombre} ${selectedEspecialista?.usuario?.apellidos}` :
                `Especialista ID: ${selectedEspecialista}`
              }
              color="primary"
              variant="outlined"
              size="small"
            />
            {selectedEspecialidad && (
              <Chip
                label={selectedEspecialidad.nombre}
                sx={{ 
                  ml: 1,
                  backgroundColor: selectedEspecialidad.color,
                  color: 'white'
                }}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Tipo de cita */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tipo de Cita
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant={tipoCita === 'individual' ? 'contained' : 'outlined'}
                    onClick={() => {
                      setTipoCita('individual');
                      // ✅ Mantener fecha/hora preseleccionadas
                      if (!formik.values.fecha && preselectedDate) {
                        formik.setFieldValue('fecha', preselectedDate);
                      }
                      if (!formik.values.hora_inicio && preselectedHour !== null) {
                        const horaInicio = new Date();
                        horaInicio.setHours(preselectedHour, 0, 0, 0);
                        formik.setFieldValue('hora_inicio', horaInicio);
                        formik.setFieldValue('hora_fin', new Date(horaInicio.getTime() + 60 * 60 * 1000));
                      }
                    }}
                    startIcon={<PersonIcon />}
                  >
                    Individual
                  </Button>
                  </Grid>
                  <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant={tipoCita === 'grupal' ? 'contained' : 'outlined'}
                    onClick={() => {
                      setTipoCita('grupal');
                      // ✅ Mantener fecha/hora preseleccionadas
                      if (!formik.values.fecha && preselectedDate) {
                        formik.setFieldValue('fecha', preselectedDate);
                      }
                      if (!formik.values.hora_inicio && preselectedHour !== null) {
                        const horaInicio = new Date();
                        horaInicio.setHours(preselectedHour, 0, 0, 0);
                        formik.setFieldValue('hora_inicio', horaInicio);
                        formik.setFieldValue('hora_fin', new Date(horaInicio.getTime() + 60 * 60 * 1000));
                      }
                    }}
                    startIcon={<GroupIcon />}
                    color="secondary"
                  >
                    Grupal
                  </Button>
                  </Grid>
                  <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant={tipoCita === 'recurrente-individual' ? 'contained' : 'outlined'}
                    onClick={() => {
                      setTipoCita('recurrente-individual');
                      // ✅ Si hay fecha/hora preseleccionadas, copiar al primer patrón
                      if (preselectedDate && !fechaInicioRecurrencia) {
                        setFechaInicioRecurrencia(preselectedDate);
                        
                        // ✅ NUEVO: Capturar día de la semana del preselectedDate
                        const diaSemana = preselectedDate.getDay(); // 0=Dom, 1=Lun, ..., 6=Sáb
                        setNuevoPatron(prev => ({
                          ...prev,
                          diaSemana: diaSemana // ← AGREGAR ESTO
                        }));
                      }
                      if (preselectedHour !== null && !nuevoPatron.horaInicio) {
                        const horaInicio = new Date();
                        horaInicio.setHours(preselectedHour, 0, 0, 0);
                        const horaFin = new Date();
                        horaFin.setHours(preselectedHour + 1, 0, 0, 0);
                        setNuevoPatron(prev => ({
                          ...prev,
                          horaInicio: horaInicio,
                          horaFin: horaFin
                        }));
                      }
                    }}
                    startIcon={<RepeatIcon />}
                    color="info"
                  >
                    Recurrente Individual
                  </Button>
                  </Grid>
                  <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant={tipoCita === 'recurrente-grupal' ? 'contained' : 'outlined'}
                    onClick={() => {
                      setTipoCita('recurrente-grupal');
                      // ✅ Si hay fecha/hora preseleccionadas, copiar al primer patrón
                      if (preselectedDate && !fechaInicioRecurrencia) {
                        setFechaInicioRecurrencia(preselectedDate);
                      }
                      if (preselectedDate && !fechaInicioRecurrencia) {
                        setFechaInicioRecurrencia(preselectedDate);
                        
                        // ✅ NUEVO: Capturar día de la semana del preselectedDate
                        const diaSemana = preselectedDate.getDay(); // 0=Dom, 1=Lun, ..., 6=Sáb
                        setNuevoPatron(prev => ({
                          ...prev,
                          diaSemana: diaSemana // ← AGREGAR ESTO
                        }));
                      }
                      if (preselectedHour !== null && !nuevoPatron.horaInicio) {
                        const horaInicio = new Date();
                        horaInicio.setHours(preselectedHour, 0, 0, 0);
                        const horaFin = new Date();
                        horaFin.setHours(preselectedHour + 1, 0, 0, 0);
                        setNuevoPatron(prev => ({
                          ...prev,
                          horaInicio: horaInicio,
                          horaFin: horaFin
                        }));
                      }
                    }}
                    startIcon={<RepeatIcon />}
                    color="success"
                  >
                    Recurrente Grupal
                  </Button>
                </Grid>
                 
                 
                 
              </Grid>
            </Box>

            {/* Paciente(s) */}
            {tipoCita.includes('individual') ? (
              <Autocomplete
                value={selectedPaciente}
                onChange={handlePacienteChange}
                options={pacientes}
                loading={loadingPacientes}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => 
                  `${option.nombres} ${option.apellidos} (HC: ${option.numero_historia})`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Seleccionar Paciente"
                    error={formik.touched.paciente_id && Boolean(formik.errors.paciente_id)}
                    helperText={formik.touched.paciente_id && formik.errors.paciente_id}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingPacientes && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <Autocomplete
                multiple
                value={pacientesSeleccionados}
                onChange={(event, newValue) => setPacientesSeleccionados(newValue)}
                options={pacientes}
                loading={loadingPacientes}
                getOptionLabel={(option) => 
                  `${option.nombres} ${option.apellidos} (HC: ${option.numero_historia})`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Seleccionar Pacientes"
                    helperText={`${pacientesSeleccionados.length} pacientes seleccionados`}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingPacientes && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={`${option.nombres} ${option.apellidos}`}
                      {...getTagProps({ index })}
                      key={option.id}
                      size="small"
                      color="secondary"
                    />
                  ))
                }
              />
            )}

            {/* Especialidad */}
            <FormControl fullWidth>
              <InputLabel>Especialidad</InputLabel>
              <Select
                value={formik.values.especialidad_id}
                onChange={handleEspecialidadChange}
                label="Especialidad"
                error={formik.touched.especialidad_id && Boolean(formik.errors.especialidad_id)}
              >
                {especialidadesFiltradas.map((esp) => (
                  <MenuItem key={esp.id} value={esp.id}>
                    {esp.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Etapa */}
            <FormControl fullWidth>
              <InputLabel>Etapa de Tratamiento</InputLabel>
              <Select
                name="etapa_id"
                value={formik.values.etapa_id}
                onChange={formik.handleChange}
                label="Etapa de Tratamiento"
                disabled={loadingEtapas}
                error={formik.touched.etapa_id && Boolean(formik.errors.etapa_id)}
              >
                {etapas.map((etapa) => (
                  <MenuItem key={etapa.id} value={etapa.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography>{etapa.nombre}</Typography>
                      {etapa.duracion_estimada && (
                        <Chip 
                          label={`${etapa.duracion_estimada} ses.`} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* SECCIÓN DE RECURRENCIA */}
            {tipoCita.includes('recurrente') && (
              <Paper elevation={0} sx={{ p: 2, bgcolor: alpha('#2196f3', 0.05), border: '1px solid', borderColor: alpha('#2196f3', 0.2) }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <RepeatIcon color="info" />
                  <Typography variant="subtitle2" color="info.main">
                    Configuración de Recurrencia
                  </Typography>
                </Stack>

                {/* Fecha inicio */}
                <DatePicker
                  label="Fecha de Inicio"
                  value={fechaInicioRecurrencia}
                  onChange={setFechaInicioRecurrencia}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      sx: { mb: 2 }
                    }
                  }}
                />

                {/* Formulario nuevo patrón */}
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Día</InputLabel>
                      <Select
                        value={nuevoPatron.diaSemana}
                        onChange={(e) => setNuevoPatron(prev => ({ ...prev, diaSemana: e.target.value }))}
                        label="Día"
                      >
                        {DIAS_SEMANA.map(dia => (
                          <MenuItem key={dia.valor} value={dia.valor}>
                            {dia.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TimePicker
                      label="Hora Inicio"
                      value={nuevoPatron.horaInicio}
                      onChange={(hora) => setNuevoPatron(prev => ({ ...prev, horaInicio: hora }))}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TimePicker
                      label="Hora Fin"
                      value={nuevoPatron.horaFin}
                      onChange={(hora) => setNuevoPatron(prev => ({ ...prev, horaFin: hora }))}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Sesiones"
                      type="number"
                      size="small"
                      fullWidth
                      value={nuevoPatron.cantidadSesiones}
                      onChange={(e) => setNuevoPatron(prev => ({ ...prev, cantidadSesiones: parseInt(e.target.value) || 1 }))}
                      inputProps={{ min: 1, max: 52 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAgregarPatron}
                      disabled={!nuevoPatron.diaSemana || !nuevoPatron.horaInicio || !nuevoPatron.horaFin}
                      fullWidth
                    >
                      <AddIcon />
                    </Button>
                  </Grid>
                </Grid>

                {/* Lista de patrones */}
                {patronesRecurrentes.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Patrones configurados ({totalSesiones} sesiones total)
                    </Typography>
                    <Stack spacing={0.5}>
                      {patronesRecurrentes.map(patron => (
                        <Paper key={patron.id} elevation={0} sx={{ p: 1, bgcolor: 'background.default' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip label={patron.diaNombre} size="small" color="info" variant="outlined" />
                              <Typography variant="caption">
                                {format(patron.horaInicio, 'HH:mm')} - {format(patron.horaFin, 'HH:mm')}
                              </Typography>
                              <Chip label={`${patron.cantidadSesiones} sesiones`} size="small" />
                            </Stack>
                            <IconButton
                              size="small"
                              onClick={() => handleEliminarPatron(patron.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* ✅ ALERTA DE FERIADOS */}
                {!validacionPatrones.validos && (
                  <Alert severity="error" sx={{ mt: 2 }} icon={<WarningIcon />}>
                    <Typography variant="subtitle2" gutterBottom>
                      ⚠️ Se detectaron {validacionPatrones.feriados.length} feriados en las fechas programadas:
                    </Typography>
                    {validacionPatrones.feriados.slice(0, 3).map((feriado, index) => (
                      <Typography key={index} variant="caption" display="block">
                        • {feriado.fecha} - {feriado.nombreFeriado} ({feriado.patron} Sesión {feriado.sesion})
                      </Typography>
                    ))}
                    {validacionPatrones.feriados.length > 3 && (
                      <Typography variant="caption" color="text.secondary">
                        ... y {validacionPatrones.feriados.length - 3} feriados más
                      </Typography>
                    )}
                  </Alert>
                )}

                {/* Preview */}
                {previewFechas.length > 0 && validacionPatrones.validos && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    ✅ Se crearán {previewFechas.length} {tipoCita === 'recurrente-grupal' ? 'sesiones grupales' : 'citas'} desde {format(previewFechas[0].fecha, 'dd/MM/yyyy')} hasta {format(previewFechas[previewFechas.length - 1].fecha, 'dd/MM/yyyy')}
                    {tipoCita === 'recurrente-grupal' && pacientesSeleccionados.length > 0 && (
                      <> • {pacientesSeleccionados.length} pacientes por sesión</>
                    )}
                  </Alert>
                )}
              </Paper>
            )}

            {/* Fecha/Hora para citas normales */}
            {!tipoCita.includes('recurrente') && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <DatePicker
                    label="Fecha"
                    value={formik.values.fecha}
                    onChange={(date) => {
                      formik.setFieldValue('fecha', date);
                      // ✅ Validar feriado en tiempo real
                      if (date) {
                        const validacion = validarFechaNoFeriado(date);
                        if (!validacion.valida) {
                          showNotification?.(validacion.mensaje, 'warning');
                        }
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: formik.touched.fecha && Boolean(formik.errors.fecha),
                        helperText: formik.touched.fecha && formik.errors.fecha
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TimePicker
                    label="Hora Inicio"
                    value={formik.values.hora_inicio}
                    onChange={handleHoraInicioChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: formik.touched.hora_inicio && Boolean(formik.errors.hora_inicio),
                        helperText: formik.touched.hora_inicio && formik.errors.hora_inicio
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TimePicker
                    label="Hora Fin"
                    value={formik.values.hora_fin}
                    onChange={(hora) => formik.setFieldValue('hora_fin', hora)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: formik.touched.hora_fin && Boolean(formik.errors.hora_fin),
                        helperText: formik.touched.hora_fin && formik.errors.hora_fin
                      }
                    }}
                  />
                </Grid>
              </Grid>
            )}

            {/* Motivo */}
            <TextField
              name="motivo"
              label="Motivo de la Consulta"
              value={formik.values.motivo}
              onChange={formik.handleChange}
              error={formik.touched.motivo && Boolean(formik.errors.motivo)}
              helperText={formik.touched.motivo && formik.errors.motivo}
              fullWidth
              multiline
              rows={2}
            />

            {/* Notas */}
            <TextField
              name="notas"
              label="Notas Adicionales (Opcional)"
              value={formik.values.notas}
              onChange={formik.handleChange}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleClose}
            disabled={loading}
            startIcon={<CloseIcon />}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formik.values.especialidad_id || !formik.values.etapa_id || !validacionPatrones.validos}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {loading ? 'Creando...' : 
             tipoCita === 'individual' ? 'Crear Cita' :
             tipoCita === 'grupal' ? `Crear Cita Grupal (${pacientesSeleccionados.length} pac.)` :
             tipoCita === 'recurrente-individual' ? `Crear ${totalSesiones} Citas Recurrentes` :
             `Crear ${totalSesiones} Sesiones Grupales (${pacientesSeleccionados.length} pac.)`
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default QuickCreateModal;